import React, { useImperativeHandle, forwardRef } from "react";
import { Box, InputBaseComponentProps, useTheme } from "@mui/material";

type StripInputProps = InputBaseComponentProps & {
  component: React.FunctionComponent<InputBaseComponentProps>;
  options: Record<string, unknown>;
};

/**
 * Receive CardNumber from Stripe as prop and useRef to explain that when you
 * use TextField it has to interact with CardNumber.
 */
const StripeInput = forwardRef<HTMLElement, StripInputProps>((props, ref) => {
  const { component: Component, options, inputRef, ...rest } = props;
  const theme = useTheme();
  const [stripeEl, setStripeEl] = React.useState<HTMLInputElement | null>(null);
  // Callers can override the element style (e.g. text color for a light field
  // background). Legacy callers pass no style, so the theme defaults apply.
  const callerStyle = (options?.style as { base?: object; invalid?: object }) || {};

  useImperativeHandle(inputRef, () => ({
    focus: () => stripeEl?.focus(),
  }), [stripeEl]);

  return (
    <Box ref={ref} sx={{ display: "flex", width: "100%" }}>
      <Component
        onReady={setStripeEl}
        options={{
          ...options,
          style: {
            base: {
              color: theme.palette.text.primary,
              fontSize: `16px`,
              lineHeight: theme.typography.body1.lineHeight,
              fontFamily: theme.typography.fontFamily,
              ...callerStyle.base,
            },
            invalid: {
              color: theme.palette.text.primary,
              ...callerStyle.invalid,
            },
          },
        }}
        {...rest}
      />
    </Box>
  )
});

export default StripeInput;
